// The captor's decision: what happens to a royal in your keeping.
//
// The four ways a captor may act — ransom, hold, execute, release — are the
// sim's four (`World::choose_capture`). The dialog only asks; the sim
// decides, and refuses anything that is not the captor's to decide.

import { Button } from '@aod/ui/components/ui/button';
import { ChoiceButton } from '@aod/ui/components/ui/choice-button';
import { SceneImage } from '@aod/ui/components/ui/scene';
import { SceneDialog } from '@aod/ui/components/ui/scene-dialog';
import { useT } from '../../i18n/index.js';
import type { MessageKey } from '../../i18n/index.js';
import { appDom } from '../../lib/appDom.js';
import type { CaptureChoice } from '../../sim/useSimWorld/applyIntent.js';
import { sceneUrls } from '../scenes.js';

export interface CaptorDialogProps {
  /** The captive in your keeping — a royal of a house, or a hero of one. */
  readonly captiveName: string;
  /** The decision the sim has already recorded, if any. */
  readonly current: CaptureChoice | null;
  /** Whether the captive is a royal of a house or a hero of one. The four
   *  choices are the same; only the wording differs. */
  readonly kind?: 'royal' | 'hero';
  readonly onChoose: (choice: CaptureChoice) => void;
  readonly onClose: () => void;
}

/** The four choices, and the word for each. The words are the same whoever
 *  is held — a ransom is a ransom. */
const CHOICE_LABELS: Record<CaptureChoice, MessageKey> = {
  ransom: 'espionage.choiceRansom',
  hold: 'espionage.choiceHold',
  execute: 'espionage.choiceExecute',
  release: 'espionage.choiceRelease',
};

/** What each choice means, for a royal. */
const ROYAL_BODIES: Record<CaptureChoice, MessageKey> = {
  ransom: 'espionage.captor.ransomBody',
  hold: 'espionage.captor.holdBody',
  execute: 'espionage.captor.executeBody',
  release: 'espionage.captor.releaseBody',
};

/** What each choice means, for a hero. */
const HERO_BODIES: Record<CaptureChoice, MessageKey> = {
  ransom: 'heroes.captor.ransomBody',
  hold: 'heroes.captor.holdBody',
  execute: 'heroes.captor.executeBody',
  release: 'heroes.captor.releaseBody',
};

/** The picture that goes with the decision on the table. */
const CHOICE_SCENES = {
  ransom: 'royal-ransom',
  hold: 'royal-captured',
  execute: 'royal-hostage-executed',
  release: 'royal-homecoming',
} as const satisfies Record<CaptureChoice, string>;

const CHOICES: readonly CaptureChoice[] = ['ransom', 'hold', 'execute', 'release'];

export function CaptorDialog({
  captiveName,
  current,
  kind = 'royal',
  onChoose,
  onClose,
}: CaptorDialogProps) {
  const t = useT();
  const hero = kind === 'hero';
  const bodies = hero ? HERO_BODIES : ROYAL_BODIES;
  return (
    <SceneDialog
      onClose={onClose}
      title={t(hero ? 'heroes.captor.title' : 'espionage.captor.title', { name: captiveName })}
      description={t(hero ? 'heroes.captor.body' : 'espionage.captor.body')}
      data-testid="captor-dialog"
      {...appDom({ id: 'captor-dialog', type: 'Modal', scope: 'dialog' })}
      media={
        <SceneImage
          {...sceneUrls('royal', CHOICE_SCENES[current ?? 'hold'])}
          widths={[384, 768]}
          sizes="(min-width: 640px) 448px, 90vw"
          aspect="strip"
          data-testid="captor-art"
        />
      }
      footer={
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          {t('espionage.captor.cancel')}
        </Button>
      }
    >
      <div className="flex flex-col gap-1.5">
        {CHOICES.map((choice) => (
          <ChoiceButton
            key={choice}
            selected={current === choice}
            title={t(CHOICE_LABELS[choice])}
            description={t(bodies[choice])}
            data-testid={`captor-choice-${choice}`}
            {...appDom({
              id: `captor-choice-${choice}`,
              type: 'Button',
              role: 'button',
              scope: 'dialog',
            })}
            onClick={() => onChoose(choice)}
          />
        ))}
      </div>
    </SceneDialog>
  );
}
